import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CalendlyEmbed from '@/components/CalendlyEmbed';
import { CheckCircle2, Zap, Calendar, Headphones, Rocket } from 'lucide-react';

const ActivationSession: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [meetingScheduled, setMeetingScheduled] = useState(false);

  const handleEventScheduled = async () => {
    setMeetingScheduled(true);
  };

  // Get prefill email from URL params if available
  const urlParams = new URLSearchParams(window.location.search);
  const prefillEmail = urlParams.get('email') || '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-brand-dark to-slate-900 flex flex-col">
      <Header />
      
      <div className="flex-1 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {meetingScheduled ? (
            /* Success State - After scheduling */
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 sm:p-10 border border-white/20">
              {/* Success Icon */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-secondary/20 mb-6">
                  <CheckCircle2 className="w-10 h-10 text-brand-secondary" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  {t('activationSession.success.title')}
                </h1>
                <p className="text-xl text-brand-secondary mb-2">
                  {t('activationSession.success.subtitle')}
                </p>
              </div>

              {/* Main Content */}
              <div className="space-y-6 text-white/90 leading-relaxed">
                <p className="text-lg text-center">
                  {t('activationSession.success.thankYou')}
                </p>

                {/* What Happens Next */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <h3 className="text-2xl font-bold text-white mb-6 text-center">
                    {t('activationSession.success.nextSteps.title')}
                  </h3>
                  
                  <div className="grid sm:grid-cols-3 gap-6">
                    <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-secondary/20 mb-4">
                        <Calendar className="w-6 h-6 text-brand-secondary" />
                      </div>
                      <h4 className="font-bold text-white mb-2">
                        {t('activationSession.success.nextSteps.step1.title')}
                      </h4>
                      <p className="text-white/70 text-sm">
                        {t('activationSession.success.nextSteps.step1.description')}
                      </p>
                    </div>

                    <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-secondary/20 mb-4">
                        <Zap className="w-6 h-6 text-brand-secondary" />
                      </div>
                      <h4 className="font-bold text-white mb-2">
                        {t('activationSession.success.nextSteps.step2.title')}
                      </h4>
                      <p className="text-white/70 text-sm">
                        {t('activationSession.success.nextSteps.step2.description')}
                      </p>
                    </div>

                    <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-secondary/20 mb-4">
                        <Rocket className="w-6 h-6 text-brand-secondary" />
                      </div>
                      <h4 className="font-bold text-white mb-2">
                        {t('activationSession.success.nextSteps.step3.title')}
                      </h4>
                      <p className="text-white/70 text-sm">
                        {t('activationSession.success.nextSteps.step3.description')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Support Info */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <div className="bg-brand-secondary/10 rounded-xl p-6 border border-brand-secondary/30 flex items-start gap-4">
                    <Headphones className="w-8 h-8 text-brand-secondary flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-white mb-2">
                        {t('activationSession.success.support.title')}
                      </h4>
                      <p className="text-white/80">
                        {t('activationSession.success.support.description')}
                      </p>
                      <p className="text-white/70 mt-3">
                        {t('activationSession.success.support.contact')}
                        <a 
                          href="mailto:agents@inscius.es" 
                          className="text-brand-secondary font-semibold hover:underline ml-1"
                        >
                          agents@inscius.es
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Return Button */}
              <div className="mt-8 text-center">
                <button
                  onClick={() => navigate('/solutions')}
                  className="px-8 py-3 bg-gradient-to-r from-brand-secondary to-brand-primary text-white rounded-lg font-medium hover:from-brand-primary hover:to-brand-secondary transition-all duration-300 hover:shadow-2xl hover:shadow-brand-secondary/40"
                >
                  {t('activationSession.success.browseMore')}
                </button>
              </div>
            </div>
          ) : (
            /* Scheduling State - Before scheduling */
            <>
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-secondary/20 mb-6">
                  <CheckCircle2 className="w-8 h-8 text-brand-secondary" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  {t('activationSession.schedule.title')}
                </h1>
                <p className="text-brand-secondary text-lg mb-2">
                  {t('activationSession.schedule.subtitle')}
                </p>
                <p className="text-white/70 max-w-2xl mx-auto">
                  {t('activationSession.schedule.description')}
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
                <div className="bg-white rounded-lg p-4">
                  <CalendlyEmbed
                    url="https://calendly.com/agents-inscius/60min"
                    prefill={{ email: prefillEmail }}
                    onEventScheduled={handleEventScheduled}
                    className="w-full"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ActivationSession;

